import React from 'react';

import { widgetsConfigurations } from 'config/widgetConfigurations/widgetsConfigurations';
import { WidgetView, WidgetViewModeUtils } from 'utilitys/reporting/widgetViewModeUtils';

import { useBaseTable } from 'hooks/useBaseTable';
import { useBaseWidget } from 'hooks/useBaseWidget';

import { ExportDropdown } from 'components/common/ExportDropdown/exportDropdown';
import { MuiIcon } from 'components/common/Icons/base/MuiIcon/MuiIcon';
import { TableV8 } from 'components/common/Tables/TableV8/tableV8';
import { TableContainer } from 'components/common/Tables/components/TableContainer/TableContainer';
import { WidgetTitle } from 'components/common/WidgetTitle/WidgetTitle';
import { WidgetWithTitle } from 'components/common/WidgetWithTitle/WidgetWithTitle';
import widgetWithTitleClasses from 'components/common/WidgetWithTitle/WidgetWithTitle.module.scss';

import { IComponentProps } from 'components/Dashboards/Project/Components/CustomDashboard/utils/utils.type';
import { AddToReport } from 'components/Dashboards/Reporting/AddToReport/AddToReport';
import { ReportingVersionAndPills } from 'components/Dashboards/Reporting/ReportingVersionAndPills/ReportingVersionAndPills';
import { Narrative } from 'components/Dashboards/Reporting/WidgetNarrativeWrapper/Narrative';
import { ThreeDotsMenu } from 'components/Dashboards/Reporting/threeDotsMenu/ThreeDotsMenu';
import { Switch } from 'components/DesignSystem/Switch';
import { CustomizedButton } from 'components/common';
import { useLogicQualityCategoriesTableLogic } from 'components/widgets/LogicQualityCategoriesTable/LogicQualityCategoriesTable.logic';
import classes from 'components/widgets/LogicQualityCategoriesTable/LogicQualityCategoriesTable.module.scss';
import { useLogicQualityCategoriesTableProvider } from 'components/widgets/LogicQualityCategoriesTable/LogicQualityCategoriesTable.provider';

export const DEFAULT_QUALITY_CATEGORY = 'All';
const { componentKey, id, endpoint } = widgetsConfigurations.allScheduleQualityChecks;

export const LogicQualityCategoriesTable = ({
    projectId,
    contractId,
    latestVersionId,
    globalFilterData,
    widgetId,
    localFilters,
    externalComponents = null,
    editNarrative = null,
    setSubTitleForExport,
    reportWidgetRef = null,
    widgetSelectedProjectAndContract = null,
    widgetViewMode = WidgetView.page,
    title,
}: IComponentProps) => {
    const { ui, api } = useLogicQualityCategoriesTableProvider({
        localFilters,
        widgetViewMode,
        projectId,
        contractId,
        latestVersionId,
        componentKey,
        globalFilterData,
        widgetId,
    });
    const titleToShow = title ?? 'Quality Category: ' + ui.vars.resolvedCategory;

    const { subTitle, route, editableTitle, setEditableTitle } = useBaseWidget({
        id,
        widgetId,
        projectId: ui.vars.resolvedProjectId,
        contractId: ui.vars.resolvedContractId,
        latestVersionId: ui.vars.selectedVersionId,
        title: titleToShow,
        setSubTitleForExport,
        ui,
        reportWidgetRef,
    });

    const { logic } = useLogicQualityCategoriesTableLogic({ ui, api });

    const { table } = useBaseTable({
        widgetId,
        localFilters,
        componentKey,
        columns: logic.generatedCols,
        widgetViewMode,
    });

    return (
        <WidgetWithTitle
            id={id}
            title={<WidgetTitle title={editableTitle} setTitle={setEditableTitle} widgetViewMode={widgetViewMode} />}
            className={WidgetViewModeUtils.isReportView(widgetViewMode) ? widgetWithTitleClasses.inReport : ''}
            titleComponents={[
                externalComponents && <div key={'externalComponents'}>{externalComponents}</div>,
                logic.shouldRenderAddToReportButton && (
                    <AddToReport
                        key={'addWidgetToReport'}
                        widgetId={widgetId}
                        componentKey={componentKey}
                        projectId={ui.vars.resolvedProjectId}
                        contractId={ui.vars.resolvedContractId}
                        uiState={ui.state}
                        shouldHide={WidgetViewModeUtils.isReportAddOrEditWidget(widgetViewMode)}
                        callBack={table.setWidgetColumns}
                        shouldUseNewIcon={true}
                        apiPayload={api.logicCheckerCategoryResults.payload}
                        widgetEndpoint={endpoint}
                    />
                ),
                !WidgetViewModeUtils.isReportAddOrEditWidget(widgetViewMode) && table.tableInstance && (
                    <ExportDropdown
                        key={'export'}
                        title={titleToShow}
                        subTitle={subTitle}
                        data={logic.generatedData}
                        tableInstance={table.tableInstance}
                        customTriggerElement={
                            <CustomizedButton variant={'text'} size={'small'}>
                                <MuiIcon fontSize={'25px'} icon={'download'} color={'#6b7280'} />
                            </CustomizedButton>
                        }
                    />
                ),
                !WidgetViewModeUtils.isReportAddOrEditWidget(widgetViewMode) && (
                    <ThreeDotsMenu
                        key={'threeDotsMenu'}
                        widgetViewMode={widgetViewMode}
                        widgetId={widgetId}
                        componentKey={componentKey}
                        shareTitle={titleToShow}
                        shareLink={route}
                        projectId={projectId}
                        contractId={contractId}
                    />
                ),
            ]}
            titleFilters={[
                <ReportingVersionAndPills
                    key={'ReportingVersionAndPills'}
                    selectedProjectAndContract={widgetSelectedProjectAndContract}
                    pills={ui.pills}
                    widgetViewMode={widgetViewMode}
                />,
                <Narrative key={'narrative'} editNarrative={editNarrative} />,
                !WidgetViewModeUtils.isReportOrPinboardView(widgetViewMode) && (
                    <div className={classes.filtersWrappers} key={'filters'}>
                        <Switch
                            onChange={(e) => {
                                ui.setters.onSetIsDcma(e.target.checked);
                            }}
                            checked={ui.state.isDcma}
                            label={'DCMA Only'}
                        />

                        <Switch
                            onChange={(e) => ui.setters.onSetIsActive(e.target.checked)}
                            checked={ui.state.isActive}
                            label={'Active Only'}
                        />

                        <Switch
                            onChange={(e) => ui.setters.onSetIsFailedChecksOnly(e.target.checked)}
                            checked={ui.state.isFailedChecksOnly}
                            label={'Failed Only'}
                        />
                    </div>
                ),
            ]}
        >
            <TableContainer>
                <TableV8
                    columns={logic.generatedCols}
                    data={logic.generatedData}
                    isLoading={logic.isLoadingTableData}
                    sorting={table.sorting}
                    setSorting={table.setSorting}
                    manualSorting={false}
                    rowSelection={table.rowSelection}
                    setRowSelection={table.setRowSelection}
                    ref={table.tableRef}
                />
            </TableContainer>
        </WidgetWithTitle>
    );
};
